import React, { useCallback, useEffect, useState, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { CircularProgress } from "@mui/material";
import Filters from "./../../../../assets/images/filters.svg";
import Threedots from "./../../../../assets/images/PhosphorLine.svg";
import Refresh from "./../../../../assets/images/ArrowCounterClockwise.svg";
import KPI from "Samarth Module/components/KPI/kpi";
import MultiSelectWithSearchInput from "Samarth Module/components/dropdown/MultiSelectWithInput";
import { fetchHighRiskSKUData, fetchDCwiseSuggested, fetchSuggestedOrderOverviewTable, handleSuggestedOrderOverviewExport, 
    handleHighRiskSkuExport, fetchSuggestedKPIs, fetchSuggestedOrderFilters, 
    fetchCustomerBucketCount} from "./../../../api/Qcommapi.js";
import { sortableColsOrderVisibility } from "Samarth Module/pages/quickComm/QuickCommStatic.js";
import OverflowMenu from "Samarth Module/components/overflowMenu/OverflowMenu";
import styles from './OrderVisibility.module.css';
import DispatchTable from "../dispatchPriority/DispatchTable";
import DCSuggestedOrders from "./DCSuggestedOrders";
import { highRiskSKUColumns } from '../QuickCommStatic';
import KPIImg from './../../../../assets/images/Phosphor.svg';
import MoreFilters from "Samarth Module/components/drawers/moreFilters";
import CustomiseColumns from "Samarth Module/components/drawers/CustomiseColumns";
import SnackBarMessage from "Samarth Module/components/snackbar/snackbar";

const OrderVisibility = ({ sideTab, handleFilterSelection }) => {
    const { accounts } = useMsal();
    const activeAccount = accounts[0];
    const [isDrawerOpen, setIsDrawerOpen] = useState({
        filterDrawer: false,
        customiseDrawer: false,
    });
    const [filters, setFilters] = useState({
        suggestedOrderFiltersData: [],
        defaultColsOverviewTable: [],
    });
    const [orderVisibilityData, setOrderVisibilityData] = useState({
        highRiskSKUData: [],
        kpiData: [],
        dcwiseSuggestedData: [],
        overviewTableData: []
    });
    const [loading, setLoading] = useState({
        "highRiskSKULoading": false,
        "kpiLoading": false,
        "dcwiseSuggestedLoading": false,
        "initialLoading": true,
        "overviewTableLoading": false,
    });
    const [sortingData, setSortingData] = useState({
        sortCol: null,
        sortDirection: null,
    });
    const [snackBarObject, setSnackBarObject] = useState({
        snackbarOpen: false,
        message: "",
        type: "error",
    });
    const clearFiltersRef = useRef(false);

    // Fetch filter options only once on initial load
    const fetchInitialData = async (suggestedOrderFilters = []) => {
        const shouldClearFilters = clearFiltersRef.current;
        clearFiltersRef.current = false;
        
        try {
            const payload = {
                email: activeAccount.username,
                "uom": sideTab || 'Tonnes',
                filters: suggestedOrderFilters.reduce((acc, filter) => {
                    acc[filter.label] = filter.selectedData;
                    return acc;
                }, {}),
                "type": "Order Visibility",
                clearFilters: shouldClearFilters,
            };

            const suggestedOrderFiltersData = await fetchSuggestedOrderFilters(payload);

            setFilters(prev => ({
                ...prev,
                suggestedOrderFiltersData: suggestedOrderFiltersData["filters"] || [],
                defaultColsOverviewTable: suggestedOrderFiltersData["defaultColumns"] || [],
            }));
            fetchData(suggestedOrderFiltersData["filters"]);
        } catch (error) {
            console.error("Error fetching initial filter data:", error);
        } finally {
            setLoading(prev => ({ ...prev, "initialLoading": false }));
        }
    };

    // Fetch all required data (except filters)
    const fetchData = async (suggestedOrderFilters = []) => {
        setLoading(prev => ({
            ...prev,
            "highRiskSKULoading": true,
            "kpiLoading": true,
            "dcwiseSuggestedLoading": true,
            "overviewTableLoading": true,
        }));
        try {
            const payload = {
                email: activeAccount.username,
                "uom": sideTab || 'Tonnes',
                filters: suggestedOrderFilters.reduce((acc, filter) => {
                    acc[filter.label] = filter.selectedData;
                    return acc;
                }, {}),
                "type": "Order Visibility",
                "expand": false,
            };

            // Execute API calls in parallel (excluding fetchDispatchSuggestedOrderFilters)
            const [kpiData, highSkuData, dcwiseSuggestedData, overviewTableData, customerBucketCount] = await Promise.all([
                fetchSuggestedKPIs(payload),
                fetchHighRiskSKUData(payload),
                fetchDCwiseSuggested(payload),
                fetchSuggestedOrderOverviewTable({...payload, page: 1, pageSize: orderVisibilityData?.paginationData?.pageSize || 15}),
                fetchCustomerBucketCount(payload), // customer bucket count api 
            ]);
            setOrderVisibilityData({
                highRiskSKUData: highSkuData["data"].map(item => ({ ...item, isExpanded: false })) || [],
                kpiData: kpiData["kpis"].map(kpi => ({
                    ...kpi,
                    img: KPIImg
                })),
                dcwiseSuggestedData: dcwiseSuggestedData["data"] || [],
                overviewTableData: overviewTableData["data"] || [],
                paginationData: overviewTableData["pagination"] || null,
                customerBucketCount: customerBucketCount['data'] || []
            });
        } catch (error) {
            setOrderVisibilityData({
                highRiskSKUData: [],
                kpiData: [],
                dcwiseSuggestedData: [],
            });
            console.error("Error fetching data:", error);
        } finally {
            setLoading(prev => ({
                ...prev,
                "highRiskSKULoading": false,
                "kpiLoading": false,
                "dcwiseSuggestedLoading": false,
                "overviewTableLoading": false,
            }));
        }
    };

    const overTableData = async (rowsPerPage, newPage, sortCol, sortDirection) => {
        // setLoading(prev => ({
        //     ...prev,
        //     "overviewTableLoading": true,
        // }));
        try {
            const payload = {
                email: activeAccount.username,
                "uom": sideTab || 'Tonnes',
                filters: filters["suggestedOrderFiltersData"].reduce((acc, filter) => {
                    acc[filter.label] = filter.selectedData;
                    return acc;
                }, {}),
                "type": "Order Visibility",
                "expand": false,
                "page": newPage,
                "pageSize": rowsPerPage,
            };
            if(sortCol && sortDirection) {
                payload.sortBy = sortCol;
                payload.sortOrder = sortDirection;
            }
            const overviewTableData = await fetchSuggestedOrderOverviewTable(payload);
            setOrderVisibilityData(prev => ({
                ...prev,
                overviewTableData: overviewTableData["data"] || [],
                paginationData: overviewTableData["pagination"] || null
            }));
        } catch (error) {
            setOrderVisibilityData(prev => ({
                ...prev,
                overviewTableData: [],
                paginationData: prev.paginationData || null,
            }));
            console.error("Error fetching overview table data:", error);
        } finally {
            // setLoading(prev => ({
            //     ...prev,
            //     "overviewTableLoading": false,
            // }));
        }
    };

    // Handle expanding/collapsing High Risk SKU rows
    const expandingCustomerskuData = async (rowData) => {
        try {
            // Find the index of the clicked row
            const clickedRowIndex = orderVisibilityData.highRiskSKUData.findIndex(
                item => item["CBU code"] === rowData["CBU code"] && item['rowType'] === 'PARENT'
            );

            if (clickedRowIndex === -1) return;

            // Check if row is currently expanded
            const currentRow = orderVisibilityData.highRiskSKUData[clickedRowIndex];
            const isCurrentlyExpanded = currentRow.isExpanded;

            if (isCurrentlyExpanded) {
                // Collapse: Remove child rows and update isExpanded to false
                const updatedData = orderVisibilityData.highRiskSKUData.filter((item, idx) => {
                    // Keep all rows except child rows of the clicked parent
                    if (item['rowType'] === 'CHILD' && item['CBU code'] === rowData['CBU code'] && idx > clickedRowIndex) {
                        return false;
                    }
                    return true;
                }).map(item => {
                    // Update isExpanded to false for the clicked parent
                    if (item["CBU code"] === rowData["CBU code"] && item['rowType'] === 'PARENT') {
                        return { ...item, isExpanded: false };
                    }
                    return item;
                });

                setOrderVisibilityData(prev => ({
                    ...prev,
                    highRiskSKUData: updatedData
                }));
            } else {
                // Expand: Fetch child rows and add them
                const payload = {
                    email: activeAccount.username,
                    "uom": sideTab || 'Tonnes',
                    filters: {
                        "CBU Description": [rowData["CBU Description"]],
                        "CBU code": [rowData["CBU code"]],
                    },
                    "type": "Order Visibility",
                    "expand": true,
                };
                const highSkuData = await fetchHighRiskSKUData(payload);
                const expandedData = highSkuData.data.filter(item => item['rowType'] !== 'PARENT') || [];

                // Update the parent row's isExpanded property and insert child rows
                const updatedData = orderVisibilityData.highRiskSKUData.flatMap((item, idx) => {
                    if (idx === clickedRowIndex) {
                        // Update parent with isExpanded true and add children after it
                        return [{ ...item, isExpanded: true }, ...expandedData];
                    }
                    return item;
                });

                setOrderVisibilityData(prev => ({
                    ...prev,
                    highRiskSKUData: updatedData
                }));
            }

        } catch (error) {
            console.error("Error fetching expanded High Risk SKU Data:", error);
        }
    };

    // Initial load - fetch filter options once
    useEffect(() => {
        if (window.location.pathname === '/samarth/order_visibility') {
            fetchInitialData();
        }
    }, []);

    // Fetch data when filters or sideTab changes
    useEffect(() => {
        if (!loading.initialLoading && filters.suggestedOrderFiltersData.length > 0) {
            fetchData();
        }
    }, [sideTab]);

    // Toggle More Filters drawer
    const toggleFilterDrawer = useCallback(() => {
        setIsDrawerOpen(prev => ({ ...prev, filterDrawer: !prev.filterDrawer }));
    }, []);

    const toggleCustomizeDrawer = useCallback(() => {
        setIsDrawerOpen(prev => ({ ...prev, customiseDrawer: !prev.customiseDrawer }));
    }, []);

    // Handle filter selection changes
    const handleFilterChange = (label, selectedOptions) => {
        let newFilter;
        setFilters(prevFilters => {
            newFilter = {
                ...prevFilters,
                suggestedOrderFiltersData: prevFilters["suggestedOrderFiltersData"].map(filter =>
                    filter.label === label
                        ? { ...filter, selectedData: selectedOptions }
                        : filter
                )
            }
        return newFilter;
        });
        
        // Check if there are any selected filters
        const hasSelection = newFilter["suggestedOrderFiltersData"].some((filter) => {
            const selected = filter?.selectedData;
            if (Array.isArray(selected)) return selected.length > 0;
            if (typeof selected === 'string') return selected.trim().length > 0;
            return selected !== undefined && selected !== null;
        });
        
        clearFiltersRef.current = !hasSelection;
        handleFilterSelection("Order Visibility", label, selectedOptions); // Pass filter changes to parent component
        fetchInitialData(newFilter["suggestedOrderFiltersData"]);
    };

    // Reset all filters - clear selectedData
    const resetFilter = () => {
        clearFiltersRef.current = true;
        let newFilter;
        setFilters(prevFilters => {
            const resetFilters = prevFilters.suggestedOrderFiltersData.map(filter => ({
                ...filter,
                selectedData: []
            }));
            newFilter = {
                ...prevFilters,
                suggestedOrderFiltersData: resetFilters
            };
            return newFilter;
        });
        setSortingData({ sortCol: null, sortDirection: null });
        fetchInitialData(newFilter['suggestedOrderFiltersData']);
    };

    const handleExport = async (label) => {
        setLoading(prev => ({ ...prev, "exportLoading": true }));
        try {
            const payload = {
                email: activeAccount.username,
                "uom": sideTab || 'Tonnes',
                filters: filters["suggestedOrderFiltersData"].reduce((acc, filter) => {
                    acc[filter.label] = filter.selectedData;
                    return acc;
                }, {}),
            };
            if (label === 'Overview') {
                payload["type"] = "Order Visibility";
                console.log("Exporting with payload:", payload);
                await handleSuggestedOrderOverviewExport(payload);
            } else {
                payload["expand"] = true;
                payload["type"] = "HighRiskSKUs"
                console.log("Exporting with payload:", payload);
                await handleHighRiskSkuExport(payload);
            }
            setSnackBarObject({ snackbarOpen: true, message: "File exported successfully", type: 'success' });
        } catch (error) {
            console.error("Error exporting data:", error);
            setSnackBarObject({ snackbarOpen: true, message: "Error occured while exporting file. Please try again later.", type: 'error' });
        } finally {
            setLoading(prev => ({ ...prev, "exportLoading": false }));
        }
    };

    const closeSnackbar = useCallback(() => {
        setSnackBarObject((prev) => ({ ...prev, snackbarOpen: false }));
    }, []);

    return (
        <>
            {loading["initialLoading"] ? <div className={styles.loaderContainer}><CircularProgress /></div> :
                <div className={`${styles.container} ${loading.exportLoading ? styles.blurBackground : ''}`}>
                    <div className={styles.sectionContainer}>
                        <KPI kpiCardData={orderVisibilityData.kpiData} sideTab={sideTab} />
                    </div>
                    <div className={styles.sectionContainer}>
                        {filters["suggestedOrderFiltersData"].filter((filter) => filter.isFavourite).map((filter, index) => (
                            <div key={`${filter.label}-${index}`} className={styles.filterWrapper}>
                                <MultiSelectWithSearchInput
                                    key={`${filter.label}-${index}`}
                                    label={filter.label}
                                    options={filter.data}
                                    filterValues={filter.selectedData}
                                    isMultiSelect={filter.isMultiSelect}
                                    handleFilter={handleFilterChange}
                                    type={'Hands Free VMI'}
                                />
                            </div>
                        ))}
                        <div className={styles.iconContainer}>
                            <img src={Refresh} className={styles.iconStyling} alt="refresh filter" onClick={resetFilter} />
                            <OverflowMenu
                                menuItems={[
                                    {
                                        label: 'More Filters',
                                        icon: Filters,
                                        onClick: toggleFilterDrawer,
                                    }
                                ]}
                                Icon={Threedots}
                            />
                        </div>
                    </div>
                    <div className={styles.middlePageContainer}>
                        <div className={styles.highRiskSKUContainer}>
                            {loading["highRiskSKULoading"] ?
                                <div className={styles.loaderContainer}><CircularProgress /></div> :
                                <DispatchTable tableData={orderVisibilityData.highRiskSKUData} label={'High Risk SKUs'} sortableColumns={sortableColsOrderVisibility} columns={highRiskSKUColumns} expandingCustomerskuData={expandingCustomerskuData}
                                 handleExport={handleExport}
                                />
                            }
                        </div>
                        <div className={styles.dcwiseSuggestedContainer}>
                            {loading["dcwiseSuggestedLoading"] ?
                                <div className={styles.loaderContainer}><CircularProgress /></div> :
                                <DCSuggestedOrders label={"DC wise suggested orders"} graphData={orderVisibilityData.dcwiseSuggestedData} customerBucketCount={orderVisibilityData.customerBucketCount} sideTab={sideTab} />
                            }
                        </div>
                    </div>
                    <div className={styles.dispatchTableContainer}>
                        {loading["overviewTableLoading"] ?
                            <div className={styles.loaderContainer}><CircularProgress /></div> :
                            <DispatchTable
                                tableData={orderVisibilityData["overviewTableData"]}
                                paginationData={orderVisibilityData["paginationData"]}
                                label={"Overview"}
                                columns={filters["defaultColsOverviewTable"]}
                                handleOpenCustomization={toggleCustomizeDrawer}
                                sortingData={sortingData}
                                handlePageChange={(rowsPerPage, newPage, sortCol, sortDirection) => {
                                    if (sortCol && sortDirection) {
                                        setSortingData({ sortCol, sortDirection });
                                    }
                                    overTableData(rowsPerPage, newPage, sortCol, sortDirection);
                                }}
                            handleExport={handleExport}
                            />
                        }
                    </div>
                    {isDrawerOpen.filterDrawer &&
                        <MoreFilters
                            anchor={'right'}
                            filterDrawer={isDrawerOpen.filterDrawer}
                            handleFilterDrawer={toggleFilterDrawer}
                            rmpmFilters={filters["suggestedOrderFiltersData"]}
                            type={"Order Visibility"}
                            setRmpmFilters={(filters) => {
                                setFilters(prevFilters => ({
                                    ...prevFilters,
                                    suggestedOrderFiltersData: filters
                                }));
                                fetchInitialData(filters);
                            }}
                            username={activeAccount.username}
                            limit={9}
                        />
                    }
                    {isDrawerOpen.customiseDrawer &&
                        <CustomiseColumns
                            anchor={'right'}
                            columns={[...filters["defaultColsOverviewTable"]]}
                            isOpen={isDrawerOpen.customiseDrawer}
                            handleCustomizeDrawer={toggleCustomizeDrawer}
                            columnCustomization={(customizedColumns) => {
                                setFilters(prevFilters => ({
                                    ...prevFilters,
                                    defaultColsOverviewTable: customizedColumns
                                }));
                                toggleCustomizeDrawer();
                            }}
                            username={activeAccount.username}
                            type="Order Visibility"
                        />
                    }
                </div>
            }
            {loading["exportLoading"] && <div className={styles.loaderExcelContainer}><CircularProgress /></div>}
            {snackBarObject.snackbarOpen && (
                <SnackBarMessage
                    type={snackBarObject.type}
                    snackbarMessage={snackBarObject.message}
                    snackbarOpen={snackBarObject.snackbarOpen}
                    setSnackbarOpen={closeSnackbar}
                />
            )}
        </>
    )
}
export default OrderVisibility;